/**
 * AGM 4종 스튜디오 설정 — 기존 폼의 모델·변수 변환·문서 생성·분류 함수를 그대로 연결(생성 결과 불변).
 *   B32204 AGM with Audited FS · B32201 AGM with Unaudited FS · B32202 Exempted (AFS) · B32203 Exempted (UFS)
 */
import { createEmptyAgmAfsForm, toAgmAfsTemplateVariables, buildAgmAfsTaskFormData, deriveAnnualReturnYearFromFye } from '../agm-afs/composables/useFormPayload';
import { buildAgmAfsPresetPrepareDocuments } from '../agm-afs/composables/buildAgmAfsPresetDocuments';
import { classifyAgmAfsDoc } from '../agm-afs/composables/agmAfsDocVariableMap';
import { applyEntityProfileToAgmAfsForm } from '../agm-afs/composables/agmAfsEntityAutofill';
import { createEmptyAgmUfsForm, toAgmUfsTemplateVariables, buildAgmUfsTaskFormData } from '../agm-ufs/composables/useFormPayload';
import { buildAgmUfsPresetPrepareDocuments } from '../agm-ufs/composables/buildAgmUfsPresetDocuments';
import { classifyAgmUfsDoc } from '../agm-ufs/composables/agmUfsDocVariableMap';
import { applyEntityProfileToAgmUfsForm } from '../agm-ufs/composables/agmUfsEntityAutofill';
import { createEmptyAgmExemptedAfsForm, toAgmExemptedAfsTemplateVariables, buildAgmExemptedAfsTaskFormData } from '../agm-exempted-afs/composables/useFormPayload';
import { buildAgmExemptedAfsPresetPrepareDocuments } from '../agm-exempted-afs/composables/buildAgmExemptedAfsPresetDocuments';
import { classifyAgmExemptedAfsDoc } from '../agm-exempted-afs/composables/agmExemptedAfsDocVariableMap';
import { applyEntityProfileToAgmExemptedAfsForm } from '../agm-exempted-afs/composables/agmExemptedAfsEntityAutofill';
import { createEmptyAgmExemptedUfsForm, toAgmExemptedUfsTemplateVariables, buildAgmExemptedUfsTaskFormData } from '../agm-exempted-ufs/composables/useFormPayload';
import { buildAgmExemptedUfsPresetPrepareDocuments } from '../agm-exempted-ufs/composables/buildAgmExemptedUfsPresetDocuments';
import { classifyAgmExemptedUfsDoc } from '../agm-exempted-ufs/composables/agmExemptedUfsDocVariableMap';
import { applyEntityProfileToAgmExemptedUfsForm } from '../agm-exempted-ufs/composables/agmExemptedUfsEntityAutofill';
import AgmNoticeDoc from './docs/AgmNoticeDoc.vue';
import AgmMinutesDoc from './docs/AgmMinutesDoc.vue';
import AgmDrAfsDoc from './docs/AgmDrAfsDoc.vue';
import AgmDrUfsDoc from './docs/AgmDrUfsDoc.vue';
import AgmConsentDoc from './docs/AgmConsentDoc.vue';
import AgmAttendanceDoc from './docs/AgmAttendanceDoc.vue';
import AgmDrExemptedDoc from './docs/AgmDrExemptedDoc.vue';
import AgmMembersResolutionDoc from './docs/AgmMembersResolutionDoc.vue';
import { memberTypeRule, type AgmStudioConfig, type AgmStudioDocDef } from './agmStudioConfig';

type F = Array<[string, string]>;
const COMPANY: F = [['company_name', 'companyName'], ['company_registration_no', 'companyRegistrationNo']];
const MEETING: F = [['registered_office_address', 'registeredOfficeAddress'], ['agm_venue', 'agmVenue'], ['agm_date', 'agmDate'], ['agm_time', 'agmTime']];
const MEMBERS: F = [['member_name', 'memberName'], ['member_representative_name', 'memberRepresentativeName']];
const DATES_AGM: F = [['notice_date', 'noticeDate'], ['consent_date', 'consentDate'], ['resolution_date', 'resolutionDate']];

/** AGM(AFS/UFS) — 공지·회의록·이사회결의·동의·출석부 */
function agmDocFor(variant: 'afs' | 'ufs') {
  return (kind: string): AgmStudioDocDef | null => {
    switch (kind) {
      case 'notice': return { comp: AgmNoticeDoc, props: { variant } };
      case 'minutes_multiple': return { comp: AgmMinutesDoc, props: { variant, kind: 'multiple' } };
      case 'minutes_sole_corporate': return { comp: AgmMinutesDoc, props: { variant, kind: 'sole_corporate' } };
      case 'minutes_sole_individual': return { comp: AgmMinutesDoc, props: { variant, kind: 'sole_individual' } };
      case 'directors_resolution': return { comp: variant === 'afs' ? AgmDrAfsDoc : AgmDrUfsDoc };
      case 'consent': return { comp: AgmConsentDoc, props: { variant } };
      case 'attendance': return { comp: AgmAttendanceDoc };
      default: return null;
    }
  };
}
function agmInclude(kind: string, model: any) {
  if (kind === 'consent') return model.isShortNotice ? { included: true } : { included: false, reason: 'Short notice is off — tick “Short notice” in the Directors’ Resolutions to include it' };
  return memberTypeRule(kind, model, 'minutes') || { included: true };
}
/** Exempted(AFS/UFS) — 이사회결의 + 회원결의(회원 유형별) */
function exemptedDocFor(variant: 'afs' | 'ufs') {
  return (kind: string): AgmStudioDocDef | null => {
    if (kind === 'directors_resolution') return { comp: AgmDrExemptedDoc, props: { variant } };
    if (kind === 'members_resolution_multiple') return { comp: AgmMembersResolutionDoc, props: { variant, kind: 'multiple' } };
    if (kind === 'members_resolution_sole_corporate') return { comp: AgmMembersResolutionDoc, props: { variant, kind: 'sole_corporate' } };
    if (kind === 'members_resolution_sole_individual') return { comp: AgmMembersResolutionDoc, props: { variant, kind: 'sole_individual' } };
    return null;
  };
}
function exemptedInclude(kind: string, model: any) {
  return memberTypeRule(kind, model, 'members_resolution') || { included: true };
}

export const AGM_AFS_STUDIO: AgmStudioConfig = {
  tag: 'PreSetAgmAfs', title: 'AGM with Audited Financial Statements', subtitle: 'AGM with Audited Financial Statements (Singapore)',
  createModel: () => createEmptyAgmAfsForm() as any,
  fieldMap: [...COMPANY, ...MEETING, ['financial_year_end_date', 'financialYearEndDate'], ['annual_return_year', 'annualReturnYear'],
    ['auditor_name', 'auditorName'], ['chairman_name', 'chairmanName'], ...MEMBERS, ...DATES_AGM],
  display: { annual_return_year: (m) => String(m.annualReturnYear || '').trim() || deriveAnnualReturnYearFromFye(m.financialYearEndDate) },
  applyProfile: applyEntityProfileToAgmAfsForm as any,
  withAuditor: true, withChairman: true,
  classify: classifyAgmAfsDoc, docFor: agmDocFor('afs'), include: agmInclude,
  toTemplateVariables: toAgmAfsTemplateVariables as any,
  buildTaskFormData: buildAgmAfsTaskFormData as any,
  buildPrepareDocuments: (i) => buildAgmAfsPresetPrepareDocuments({ ...i, isShortNotice: !!i.formModel.isShortNotice, memberType: i.formModel.memberType } as any) as any,
  snapshot: 'nested',
};

export const AGM_UFS_STUDIO: AgmStudioConfig = {
  tag: 'PreSetAgmUfs', title: 'AGM with Unaudited Financial Statements', subtitle: 'AGM with Unaudited Financial Statements (Singapore)',
  createModel: () => createEmptyAgmUfsForm() as any,
  fieldMap: [...COMPANY, ...MEETING, ['financial_year_end_date', 'financialYearEndDate'], ['chairman_name', 'chairmanName'], ...MEMBERS, ...DATES_AGM],
  applyProfile: applyEntityProfileToAgmUfsForm as any,
  withAuditor: false, withChairman: true,
  classify: classifyAgmUfsDoc, docFor: agmDocFor('ufs'), include: agmInclude,
  toTemplateVariables: toAgmUfsTemplateVariables as any,
  buildTaskFormData: buildAgmUfsTaskFormData as any,
  buildPrepareDocuments: (i) => buildAgmUfsPresetPrepareDocuments({ ...i, isShortNotice: !!i.formModel.isShortNotice, memberType: i.formModel.memberType } as any) as any,
  snapshot: 'nested',
};

export const AGM_EXEMPTED_AFS_STUDIO: AgmStudioConfig = {
  tag: 'PreSetAgmExemptedAfs', title: 'AGM Exempted with Audited Financial Statements', subtitle: 'AGM Exempted (s.175A) with Audited Financial Statements (Singapore)',
  createModel: () => createEmptyAgmExemptedAfsForm() as any,
  fieldMap: [...COMPANY, ['financial_year_end_date', 'financialYearEndDate'], ['annual_return_year', 'annualReturnYear'],
    ['deemed_agm_date', 'deemedAgmDate'], ['resolution_date', 'resolutionDate'], ['auditor_name', 'auditorName'], ...MEMBERS],
  applyProfile: applyEntityProfileToAgmExemptedAfsForm as any,
  withAuditor: true, withChairman: false,
  classify: classifyAgmExemptedAfsDoc, docFor: exemptedDocFor('afs'), include: exemptedInclude,
  toTemplateVariables: toAgmExemptedAfsTemplateVariables as any,
  buildTaskFormData: buildAgmExemptedAfsTaskFormData as any,
  buildPrepareDocuments: (i) => buildAgmExemptedAfsPresetPrepareDocuments({ ...i, memberType: i.formModel.memberType } as any) as any,
  snapshot: 'flat',
};

export const AGM_EXEMPTED_UFS_STUDIO: AgmStudioConfig = {
  tag: 'PreSetAgmExemptedUfs', title: 'AGM Exempted with Unaudited Financial Statements', subtitle: 'AGM Exempted (s.175A) with Unaudited Financial Statements (Singapore)',
  createModel: () => createEmptyAgmExemptedUfsForm() as any,
  fieldMap: [...COMPANY, ['financial_year_end_date', 'financialYearEndDate'], ['annual_return_year', 'annualReturnYear'],
    ['deemed_agm_date', 'deemedAgmDate'], ['resolution_date', 'resolutionDate'], ...MEMBERS],
  applyProfile: applyEntityProfileToAgmExemptedUfsForm as any,
  withAuditor: false, withChairman: false,
  classify: classifyAgmExemptedUfsDoc, docFor: exemptedDocFor('ufs'), include: exemptedInclude,
  toTemplateVariables: toAgmExemptedUfsTemplateVariables as any,
  buildTaskFormData: buildAgmExemptedUfsTaskFormData as any,
  buildPrepareDocuments: (i) => buildAgmExemptedUfsPresetPrepareDocuments({ ...i, memberType: i.formModel.memberType } as any) as any,
  snapshot: 'flat',
};
